// ComposedExperienceAurora — Tier-A "aurora" skin for composed experiences.
//
// The second per-world treatment of the composed runtime (after Obsidian),
// proving the dispatcher pattern generalises. Same data + capture contract as
// ComposedExperienceSlide (both consume useComposedExperience), rendered in the
// AURORA visual language shared with AuroraBeforeAfterBridge: a night sky, frost
// text, frosted-glass panels, green→teal→violet gradients, and — the standout —
// a re-rate rate-slider that flows a luminous RIBBON from the learner's first
// reading to their revised one, blooming on commit, so a change of mind is made
// visible (§8.12b: juice = cognition). Aurora's signature is the ribbon/bloom,
// where Obsidian's was the red string.

import { useEffect, useMemo, useState } from 'react';
import type { Theme } from '../../slideTypes';
import type { ComposedExperienceSlideData } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type {
  ChoiceBlock,
  CommitBoxBlock,
  CompositionBlock,
  ConfidenceBlock,
  FlagInTextBlock,
  PerspectiveLensBlock,
  RateSliderBlock,
  RevealBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
  StimulusBlock,
} from '../../../../../lib/scenario-builder/composition/types';
import type { Answers, BlockAnswer } from '../../../../../lib/scenario-builder/composition/capture';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import {
  useThemeWorldAudio,
  playAuroraPluck,
  playAuroraShiftBloom,
} from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';
import { useComposedExperience } from './useComposedExperience';

interface Props {
  data: ComposedExperienceSlideData;
  theme: Theme;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

// Aurora palette (mockup-faithful constants, matching AuroraBeforeAfterBridge).
const GREEN = '#5ef2a0';
const TEAL = '#15c4b8';
const VIOLET = '#8b6cff';
const NIGHT = '#0a0e1f';
const FROST = '#eaf4ff';
const FROST_DIM = '#9fb3d9';

const STYLE_ID = 'aurora-composed-scoped-styles';

// A cue runner bound to the aurora engine (from useThemeWorldAudio.playWith).
type PlayWith = ReturnType<typeof useThemeWorldAudio>['playWith'];

function scopedCss(): string {
  return `
.aurcx { position: relative; }
.aurcx * { box-sizing: border-box; }

.aurcx .aur-glass {
  background: rgba(20,30,60,0.34); backdrop-filter: blur(20px) saturate(1.5); -webkit-backdrop-filter: blur(20px) saturate(1.5);
  border: 1px solid rgba(234,244,255,0.16); border-radius: 22px;
  box-shadow: 0 16px 50px rgba(0,0,0,0.32), inset 0 1px 0 rgba(234,244,255,0.07);
  position: relative; padding: 22px 24px;
}
.aurcx .aur-kicker { font-size: 0.72rem; letter-spacing: 0.2em; text-transform: uppercase; color: ${GREEN}; margin-bottom: 8px; font-weight: 600; }
.aurcx .aur-prompt { font-family: inherit; font-size: 1.05rem; color: ${FROST}; margin: 0 0 6px; line-height: 1.4; }
.aurcx .aur-body { color: ${FROST}; font-size: 1rem; line-height: 1.6; margin: 0; }
.aurcx .aur-quote { border-left: 3px solid ${VIOLET}; padding: 6px 0 6px 16px; color: ${FROST}; }

.aurcx textarea.aur-input {
  width: 100%; font-family: inherit; font-size: 1rem; color: ${FROST}; line-height: 1.55;
  background: rgba(10,14,31,0.4); border: 1px solid rgba(234,244,255,0.14); border-radius: 14px; padding: 12px 14px; resize: vertical;
  transition: border-color .3s, box-shadow .3s;
}
.aurcx textarea.aur-input:focus { outline: none; border-color: ${GREEN}; box-shadow: 0 0 0 3px rgba(94,242,160,0.14); }

/* gauge */
.aurcx .aur-scale-val { font-size: 2.6rem; line-height: 1; color: ${FROST}; text-shadow: 0 0 24px rgba(94,242,160,0.5); text-align: center; }
.aurcx input[type="range"].aur-range { -webkit-appearance: none; appearance: none; width: 100%; height: 8px; border-radius: 999px; outline: none; cursor: grab; border: 1px solid rgba(234,244,255,0.1); background: rgba(234,244,255,0.1); }
.aurcx input[type="range"].aur-range:disabled { cursor: default; }
.aurcx input[type="range"].aur-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; border-radius: 50%; background: radial-gradient(circle at 35% 30%, #fff, ${GREEN} 55%, ${TEAL}); border: 2px solid rgba(255,255,255,0.85); box-shadow: 0 0 0 6px rgba(94,242,160,0.18), 0 0 20px rgba(94,242,160,0.65); }
.aurcx input[type="range"].aur-range::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50%; border: 2px solid rgba(255,255,255,0.85); background: radial-gradient(circle at 35% 30%, #fff, ${GREEN} 55%, ${TEAL}); box-shadow: 0 0 0 6px rgba(94,242,160,0.18), 0 0 20px rgba(94,242,160,0.65); }

/* the change-of-mind RIBBON (re-rate standout) */
.aurcx .aur-track { position: relative; height: 44px; margin: 16px 0 6px; }
.aurcx .aur-rail { position: absolute; top: 50%; left: 0; right: 0; height: 8px; transform: translateY(-50%); border-radius: 999px; background: rgba(234,244,255,0.1); border: 1px solid rgba(234,244,255,0.1); }
.aurcx .aur-range.aur-overlay { position: absolute; inset: 0; height: 100%; background: transparent; border: none; z-index: 4; }
.aurcx .aur-prior { position: absolute; top: 50%; width: 16px; height: 16px; border-radius: 50%; transform: translate(-50%,-50%); border: 2px solid ${FROST_DIM}; background: rgba(10,14,31,0.6); z-index: 3; }
.aurcx .aur-ribbon {
  position: absolute; top: 50%; height: 10px; transform: translateY(-50%); border-radius: 999px; z-index: 2;
  background: linear-gradient(90deg, ${GREEN}, ${TEAL}, ${VIOLET});
  box-shadow: 0 0 18px rgba(94,242,160,0.6), 0 0 30px rgba(139,108,255,0.35);
  transition: left .55s cubic-bezier(0.22,0.61,0.27,1), width .55s cubic-bezier(0.22,0.61,0.27,1);
}
.aurcx .aur-ribbon.bloom::after { content: ""; position: absolute; inset: -8px; border-radius: 999px; background: radial-gradient(circle, rgba(94,242,160,0.35), transparent 70%); animation: aurBloom 2.4s ease forwards; }
@keyframes aurBloom { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0.35; } }
.aurcx .aur-delta { font-size: 0.8rem; letter-spacing: 0.06em; color: ${GREEN}; text-shadow: 0 0 14px rgba(94,242,160,0.5); margin-top: 10px; font-weight: 600; }
.aurcx .aur-ends { display: flex; justify-content: space-between; font-size: 0.74rem; color: ${FROST_DIM}; margin-top: 2px; }

.aurcx .aur-btn {
  margin-top: 14px; font-size: 0.92rem; font-weight: 500; letter-spacing: 0.02em; color: ${NIGHT}; cursor: pointer;
  padding: 11px 24px; border-radius: 999px; border: none; background: linear-gradient(110deg, ${GREEN}, ${TEAL});
  box-shadow: 0 6px 24px rgba(21,196,184,0.4); transition: transform .35s cubic-bezier(0.22,0.61,0.27,1), box-shadow .35s, opacity .3s;
}
.aurcx .aur-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 10px 34px rgba(94,242,160,0.55); }
.aurcx .aur-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* options */
.aurcx .aur-opt {
  width: 100%; text-align: left; color: ${FROST}; background: rgba(10,14,31,0.4); border: 1px solid rgba(234,244,255,0.14);
  border-radius: 14px; padding: 12px 14px; font: inherit; cursor: pointer; transition: border-color .25s, box-shadow .25s, background .25s;
}
.aurcx .aur-opt:hover:not(:disabled) { border-color: rgba(94,242,160,0.5); }
.aurcx .aur-opt.aur-on { border-color: ${GREEN}; background: rgba(94,242,160,0.12); box-shadow: 0 0 0 2px rgba(94,242,160,0.25), 0 0 24px rgba(94,242,160,0.25); }
.aurcx .aur-opt:disabled { cursor: default; }

/* reveal — folds open with a bloom rather than a hard cut */
.aurcx .aur-sealed { border: 1px solid rgba(139,108,255,0.28); border-radius: 18px; padding: 20px; text-align: center; color: ${FROST_DIM}; font-size: 0.86rem; letter-spacing: 0.08em; background: rgba(139,108,255,0.08); }
.aurcx .aur-reveal { opacity: 0; transform: translateY(20px); animation: aurRise .8s cubic-bezier(0.22,0.61,0.27,1) forwards; overflow: hidden; }
.aurcx .aur-reveal::before { content: ""; position: absolute; inset: 0; border-radius: inherit; background: radial-gradient(120% 120% at 0% 0%, rgba(94,242,160,0.18), transparent 55%), radial-gradient(120% 120% at 100% 100%, rgba(139,108,255,0.16), transparent 55%); animation: aurBloom 2.6s ease forwards; pointer-events: none; }
@keyframes aurRise { to { opacity: 1; transform: translateY(0); } }

/* ending */
.aurcx .aur-settle { text-align: center; margin-bottom: 14px; }
.aurcx .aur-settle span { font-size: 0.74rem; letter-spacing: 0.28em; text-transform: uppercase; color: ${GREEN}; text-shadow: 0 0 18px rgba(94,242,160,0.6); }
.aurcx .aur-settle .aur-arc { height: 2px; width: 120px; margin: 12px auto 0; border-radius: 999px; background: linear-gradient(90deg, transparent, ${GREEN}, ${VIOLET}, transparent); box-shadow: 0 0 16px rgba(94,242,160,0.5); }

@media (prefers-reduced-motion: reduce) {
  .aurcx .aur-ribbon, .aurcx .aur-reveal { transition: none !important; animation-duration: .001s !important; }
  .aurcx .aur-ribbon.bloom::after, .aurcx .aur-reveal::before { animation: none !important; opacity: 0.3; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────

export function ComposedExperienceAurora({
  data,
  theme,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  useScopedStyles();

  const { blocks, answers, setAnswer, complete, values, rollup } = useComposedExperience({
    data,
    isEditing,
    onActivityResponse,
    onComplete,
    savedResponse,
  });

  const { enabled: audioOn, toggle: toggleAudio, playWith } = useThemeWorldAudio('aurora');

  return (
    <div
      className="aurcx h-full w-full overflow-y-auto"
      style={{
        background: `radial-gradient(1200px 700px at 50% -5%, rgba(94,242,160,0.12), transparent 60%), radial-gradient(900px 600px at 100% 20%, rgba(139,108,255,0.14), transparent 55%), ${NIGHT}`,
        color: FROST,
        fontFamily: theme.fonts.body,
      }}
    >
      <ThemeWorldAtmosphere worldId="aurora" primary={TEAL} accent={VIOLET} background={NIGHT} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div className="mx-auto" style={{ maxWidth: 720, padding: '56px 24px 110px' }}>
          <header style={{ marginBottom: 30 }}>
            <div style={{ fontFamily: theme.fonts.code, fontSize: 11, letterSpacing: '0.34em', color: GREEN, textTransform: 'uppercase', marginBottom: 14 }}>
              ✦ A reflection
            </div>
            <h1 style={{ margin: 0 }}>
              <Typewriter
                text={data.title}
                speed={30}
                caretColor={GREEN}
                style={{
                  fontFamily: theme.fonts.heading,
                  fontWeight: 600,
                  fontSize: 'clamp(1.7rem, 4.4vw, 2.7rem)',
                  lineHeight: 1.08,
                  color: FROST,
                  textShadow: `0 0 30px rgba(94,242,160,0.4)`,
                  display: 'inline-block',
                }}
              />
            </h1>
            {data.subtitle && <p style={{ color: FROST_DIM, fontSize: 15, margin: '12px 0 0' }}>{data.subtitle}</p>}
          </header>

          <div className="space-y-5">
            {blocks.map((block) => (
              <AurBlock
                key={block.id}
                block={block}
                answers={answers}
                answer={answers[block.id]}
                onAnswer={(a) => setAnswer(block.id, a)}
                playWith={playWith}
              />
            ))}
          </div>

          {complete && data.conclusion && (
            <div className="mt-10">
              <div className="aur-settle">
                <span>The sky settles</span>
                <div className="aur-arc" />
              </div>
              <ExperienceClose
                conclusion={data.conclusion}
                theme={theme}
                values={values}
                outcome={data.scoring.mode === 'none' ? undefined : String(Math.round(rollup * 100))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface AurBlockProps {
  block: CompositionBlock;
  answers: Answers;
  answer: BlockAnswer | undefined;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}

function AurBlock({ block, answers, answer, onAnswer, playWith }: AurBlockProps) {
  switch (block.type) {
    case 'stimulus':
      return <AurStimulus block={block} />;
    case 'rate-slider':
      return <AurRateSlider block={block} answers={answers} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'confidence':
      return <AurRateSlider block={block as unknown as RateSliderBlock} answers={answers} answer={answer} onAnswer={onAnswer} playWith={playWith} confidence />;
    case 'reveal':
      return <AurReveal block={block} answers={answers} playWith={playWith} />;
    case 'commit-box':
      return <AurCommitBox block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'choice':
      return <AurChoice block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'select-evidence':
      return <AurSelectEvidence block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'sort-zones':
      return <AurSortZones block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'flag-in-text':
      return <AurFlagInText block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'perspective-lens':
      return <AurPerspective block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    default:
      return null;
  }
}

function Glass({ children }: { children: React.ReactNode }) {
  return <div className="aur-glass">{children}</div>;
}

// ── stimulus ──────────────────────────────────────────────────────────────────
function AurStimulus({ block }: { block: StimulusBlock }) {
  return (
    <Glass>
      {block.content.heading && <div className="aur-kicker">{block.content.heading}</div>}
      <p className="aur-body">{block.content.body}</p>
    </Glass>
  );
}

// ── rate-slider (+ confidence) — the change-of-mind RIBBON standout ────────────
function AurRateSlider({
  block,
  answers,
  answer,
  onAnswer,
  playWith,
  confidence,
}: {
  block: RateSliderBlock;
  answers: Answers;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
  confidence?: boolean;
}) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const locked = committed !== undefined;
  const value = locked ? committed : pos;
  const prior = block.ref ? (answers[block.ref] as { value?: number } | undefined)?.value : undefined;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const delta = prior !== undefined ? value - prior : undefined;

  return (
    <Glass>
      <div className="aur-kicker">{confidence ? 'Confidence' : prior !== undefined ? 'Where you land now' : 'Your reading'}</div>
      <p className="aur-prompt">{block.content.prompt}</p>
      <div className="aur-scale-val">{value}</div>
      <div className="aur-track">
        <div className="aur-rail" />
        {prior !== undefined && (
          <span
            className={`aur-ribbon${locked ? ' bloom' : ''}`}
            style={{ left: `${Math.min(pct(prior), pct(value))}%`, width: `${Math.abs(pct(value) - pct(prior))}%` }}
            aria-hidden
          />
        )}
        {prior !== undefined && <span className="aur-prior" style={{ left: `${pct(prior)}%` }} aria-hidden />}
        <input
          type="range"
          className="aur-range aur-overlay"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          disabled={locked}
          onChange={(e) => {
            const v = Number(e.target.value);
            setPos(v);
            playWith((eng) => playAuroraPluck(eng, (v - min) / (max - min)));
          }}
        />
      </div>
      <div className="aur-ends">
        <span>{block.content.minLabel ?? min}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {prior !== undefined && delta !== undefined && (
        <div className="aur-delta">
          {delta === 0 ? 'You held steady' : `You shifted ${delta > 0 ? '+' : ''}${delta} — from ${prior} to ${value}`}
        </div>
      )}
      {!locked && (
        <button
          className="aur-btn"
          onClick={() => {
            playWith((eng) => playAuroraShiftBloom(eng, prior !== undefined ? 1.1 : 0.7));
            onAnswer({ value: pos });
          }}
        >
          {prior !== undefined ? 'Settle here' : 'Set your reading'}
        </button>
      )}
    </Glass>
  );
}

// ── reveal — folds open with a bloom once its ref commits ──────────────────────
function AurReveal({ block, answers, playWith }: { block: RevealBlock; answers: Answers; playWith: PlayWith }) {
  const unlocked = answers[block.ref] !== undefined;
  useEffect(() => {
    if (unlocked) playWith((eng) => playAuroraShiftBloom(eng, 1));
  }, [unlocked, playWith]);
  if (!unlocked) {
    return <div className="aur-sealed">✦ Settle your reading to let the wider view open ✦</div>;
  }
  return (
    <div className="aur-glass aur-reveal">
      <div className="aur-kicker">{block.content.heading || 'The wider view'}</div>
      <p className="aur-body">{block.content.body}</p>
    </div>
  );
}

// ── commit-box — held, then set into a soft quote ─────────────────────────────
function AurCommitBox({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: CommitBoxBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { text: string } | undefined)?.text;
  const [text, setText] = useState(committed ?? '');
  const locked = committed !== undefined;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const ok = words >= (block.structure.minWords ?? 1);
  return (
    <Glass>
      <div className="aur-kicker">What you'll hold</div>
      <p className="aur-prompt">{block.content.prompt}</p>
      {locked ? (
        <div className="aur-quote">{committed}</div>
      ) : (
        <>
          <textarea className="aur-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={block.content.placeholder} />
          <button
            className="aur-btn"
            disabled={!ok}
            onClick={() => {
              playWith((eng) => playAuroraShiftBloom(eng, 0.9));
              onAnswer({ text: text.trim() });
            }}
          >
            Hold this
          </button>
        </>
      )}
    </Glass>
  );
}

// ── choice ────────────────────────────────────────────────────────────────────
function AurChoice({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: ChoiceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const chosen = (answer as { optionId: string } | undefined)?.optionId;
  const chosenOpt = block.content.options.find((o) => o.id === chosen);
  return (
    <Glass>
      <p className="aur-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.options.map((opt) => (
          <button
            key={opt.id}
            disabled={!!chosen}
            onClick={() => {
              playWith((eng) => playAuroraPluck(eng, 0.6));
              onAnswer({ optionId: opt.id });
            }}
            className={`aur-opt${opt.id === chosen ? ' aur-on' : ''}`}
            style={{ opacity: chosen && opt.id !== chosen ? 0.5 : 1 }}
          >
            {opt.text}
          </button>
        ))}
      </div>
      {chosenOpt?.feedback && <p style={{ marginTop: 12, fontSize: '0.9rem', color: FROST_DIM, fontStyle: 'italic' }}>{chosenOpt.feedback}</p>}
    </Glass>
  );
}

// ── select-evidence — glowing selections ──────────────────────────────────────
function AurSelectEvidence({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: SelectEvidenceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { selected: string[] } | undefined)?.selected;
  const [selected, setSelected] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const max = block.structure.maxSelections;
  const atMax = max !== undefined && selected.length >= max;
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : atMax ? s : [...s, id]));
  return (
    <Glass>
      {block.content.claim && <div className="aur-quote" style={{ marginBottom: 12 }}>{block.content.claim}</div>}
      <p className="aur-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.items.map((it) => (
          <button
            key={it.id}
            disabled={locked || (!selected.includes(it.id) && atMax)}
            onClick={() => {
              playWith((eng) => playAuroraPluck(eng, 0.6));
              toggle(it.id);
            }}
            className={`aur-opt${selected.includes(it.id) ? ' aur-on' : ''}`}
          >
            {it.text}
          </button>
        ))}
      </div>
      {max !== undefined && !locked && <p style={{ marginTop: 8, fontSize: '0.72rem', color: FROST_DIM }}>{selected.length}/{max} chosen</p>}
      {!locked && (
        <button className="aur-btn" disabled={selected.length === 0} onClick={() => onAnswer({ selected })}>
          Settle on these
        </button>
      )}
    </Glass>
  );
}

// ── sort-zones ────────────────────────────────────────────────────────────────
function AurSortZones({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: SortZonesBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { placement: Record<string, string> } | undefined)?.placement;
  const [placement, setPlacement] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allPlaced = block.content.items.every((it) => placement[it.id]);
  return (
    <Glass>
      <p className="aur-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.items.map((it) => (
          <div key={it.id}>
            <p style={{ fontSize: '0.92rem', color: FROST, margin: '0 0 6px' }}>{it.text}</p>
            <div className="flex flex-wrap gap-2">
              {block.content.zones.map((z) => (
                <button
                  key={z.id}
                  disabled={locked}
                  onClick={() => {
                    playWith((eng) => playAuroraPluck(eng, 0.6));
                    setPlacement((p) => ({ ...p, [it.id]: z.id }));
                  }}
                  className={`aur-opt${placement[it.id] === z.id ? ' aur-on' : ''}`}
                  style={{ width: 'auto', padding: '6px 14px', fontSize: '0.82rem', borderRadius: 999 }}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!locked && (
        <button className="aur-btn" disabled={!allPlaced} onClick={() => onAnswer({ placement })}>
          Settle the sort
        </button>
      )}
    </Glass>
  );
}

// ── flag-in-text ──────────────────────────────────────────────────────────────
function AurFlagInText({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: FlagInTextBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { flagged: string[] } | undefined)?.flagged;
  const [flagged, setFlagged] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const toggle = (id: string) => setFlagged((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  return (
    <Glass>
      <p className="aur-prompt">{block.content.prompt}</p>
      <p className="aur-body" style={{ lineHeight: 1.9 }}>
        {block.content.segments.map((seg) => {
          const on = flagged.includes(seg.id);
          const wasRight = locked && seg.flagged === true;
          const wasWrong = locked && on && seg.flagged !== true;
          return (
            <span
              key={seg.id}
              onClick={() => {
                if (locked) return;
                playWith((eng) => playAuroraPluck(eng, 0.6));
                toggle(seg.id);
              }}
              style={{
                cursor: locked ? 'default' : 'pointer',
                background: on ? (wasWrong ? 'rgba(139,108,255,0.22)' : 'rgba(94,242,160,0.18)') : 'transparent',
                borderBottom: locked
                  ? `2px solid ${wasRight ? GREEN : wasWrong ? VIOLET : 'transparent'}`
                  : on
                    ? `2px solid ${GREEN}`
                    : '2px solid transparent',
                borderRadius: 3,
                padding: '0 2px',
              }}
            >
              {seg.text}{' '}
            </span>
          );
        })}
      </p>
      {!locked && (
        <button className="aur-btn" disabled={flagged.length === 0} onClick={() => onAnswer({ flagged })}>
          Mark {flagged.length} span{flagged.length === 1 ? '' : 's'}
        </button>
      )}
    </Glass>
  );
}

// ── perspective-lens ──────────────────────────────────────────────────────────
function AurPerspective({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: PerspectiveLensBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { takes: Record<string, string> } | undefined)?.takes;
  const [takes, setTakes] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allFilled = block.content.lenses.every((l) => (takes[l.id] ?? '').trim().length > 0);
  return (
    <Glass>
      <p className="aur-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.lenses.map((lens) => (
          <div key={lens.id}>
            <p style={{ fontSize: '0.82rem', fontWeight: 600, color: GREEN, letterSpacing: '0.08em', margin: '0 0 3px' }}>{lens.label}</p>
            {lens.hint && <p style={{ fontSize: '0.78rem', color: FROST_DIM, margin: '0 0 4px' }}>{lens.hint}</p>}
            {locked ? (
              <div className="aur-quote">{takes[lens.id]}</div>
            ) : (
              <textarea
                className="aur-input"
                rows={2}
                value={takes[lens.id] ?? ''}
                onChange={(e) => setTakes((tk) => ({ ...tk, [lens.id]: e.target.value }))}
              />
            )}
          </div>
        ))}
      </div>
      {!locked && (
        <button className="aur-btn" disabled={!allFilled} onClick={() => onAnswer({ takes })}>
          Hold these
        </button>
      )}
    </Glass>
  );
}
